import React from 'react';
import { Radio, Loader2, RefreshCw } from 'lucide-react';
import { Monitor } from '../../../../types/monitoring';
import { MonitorListItem } from './MonitorListItem';
import { Button } from '../../../ui/Button';

interface MonitorListProps {
  monitors: Monitor[];
  isLoading: boolean;
  onSelectMonitor: (monitor: Monitor) => void;
  onRefresh: () => void;
}

export const MonitorList: React.FC<MonitorListProps> = ({
  monitors,
  isLoading,
  onSelectMonitor,
  onRefresh,
}) => {
  const activeCount = monitors.filter(m => m.status === 'enabled').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
      </div>
    );
  }

  if (monitors.length === 0) {
    return (
      <div className="py-16 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-neutral-100 flex items-center justify-center">
          <Radio className="w-7 h-7 text-neutral-400" />
        </div>
        <p className="text-[15px] font-medium text-neutral-900 mb-1">No monitors</p>
        <p className="text-[13px] text-neutral-500 max-w-[260px] mx-auto">
          Create a monitor from any dataset to automatically discover new leads.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[13px] text-neutral-600">
          <span className="font-semibold text-neutral-900">{activeCount}</span> of {monitors.length} active
        </span>
        <Button
          id="refresh-monitors"
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          aria-label="Refresh monitors list"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="space-y-2">
        {monitors.map((monitor) => (
          <MonitorListItem
            key={monitor.id}
            monitor={monitor}
            onClick={() => onSelectMonitor(monitor)}
          />
        ))}
      </div>
    </div>
  );
};
